import { Dispatch, SetStateAction, useState } from 'react';
import { QueryObserverResult } from 'react-query';

import { EyeIcon } from '../../../../assets/svg';
import { RecurrentPayIcon } from '../../../../assets/svg/payment-links-types/RecurrentPay';
import { UniquePayIcon } from '../../../../assets/svg/payment-links-types/UniquePay';
import { ShareIcon } from '../../../../assets/svg/ShareIcon';
import { ETPayButton } from '../../../../components/button';
import { DateAndTime } from '../../../../components/date-and-time';
import { Select } from '../../../../components/form/select';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { Status } from '../../../../components/status';
import { Tooltip } from '../../../../components/tooltip';
import { defaultTheme } from '../../../../config/theme';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { PaymentStatus, Status as StatusType } from '../../../../models/common-payments';
import { PaymentMultiuse } from '../../../../models/payment-links-multiuse';
import { ViewsAllowed } from '../../../../models/payment-links-multiuse';
import { ColumnInterface, Filters, Pagination, RowInterface } from '../../../../models/table';
import { PaymentLinksMultiuseServices } from '../../../../services/payment-links-multiuse';
import { hasPermission } from '../../../../utils/common';
import { errorFormatter } from '../../../../utils/errors';
import { fillStatesWithZeros } from '../../../../utils/states';
import { Dropdown } from '../dropdown';
import { ModalMultiuse } from '../modal';
import { NewSinglePayment } from '../new-single-payment';
import { PLTable } from '../pl-table';
import { PaymentsMultiuse, TableDetail } from '../table-detail';
import { ContainerActionsComponent } from './ContainerActionsComponent';
import {
	AmountContainer,
	ContainerActions,
	ExportModalContent,
	ExportModalDescription,
	ExportModalTitle,
	ProductFilterLabel,
	SelectContainer,
	SimpleButton,
	StatusContainer,
	Text,
	TextPayment,
	VoucherButton,
	VoucherContainer,
} from './styles';

interface PaymentLinkTableProps {
	data: PaymentMultiuse[];
	isLoading?: boolean;
	stateList: PaymentStatus[];
	filters: Filters;
	onFilterChange: Dispatch<SetStateAction<Filters>>;
	pagination: Pagination;
	setPagination: Dispatch<SetStateAction<Pagination>>;
	totalResults?: number;
	refetch: () => Promise<QueryObserverResult<any, unknown>>;
}

export interface ModalProps {
	loader: boolean;
	show: boolean;
	error: boolean;
	errorMessage: string;
	retryFunction?: () => void;
	url: string;
}

const defaultModal: ModalProps = {
	loader: false,
	show: false,
	error: false,
	errorMessage: '',
	url: '',
};

interface ShareModalState {
	show: boolean;
	selected: string;
}

interface Status {
	status: string;
	message: string;
}

interface GetStatusMessage {
	status: string;
	numpayments: number;
	endexpiredatdate: string;
	endexpiredattime: string;
	initexpiredatdate: string;
	initexpiredattime: string;
}

const PaymentLinkTable = ({
	data,
	isLoading,
	stateList,
	filters,
	onFilterChange,
	pagination,
	setPagination,
	totalResults,
	refetch,
}: PaymentLinkTableProps) => {
	const { merchantSelected } = useMerchant();
	const [modal, setModal] = useState<ModalProps>(defaultModal);
	const { handleShowAlert } = useFloatingAlert();
	const [showEditModal, setShowEditModal] = useState(false);
	const [selectedRowData, setSelectedRowData] = useState<PaymentMultiuse | undefined>(undefined);
	const [showShareModal, setShowShareModal] = useState<ShareModalState>({
		show: false,
		selected: '',
	});
	const [viewType, setViewType] = useState<ViewsAllowed>('create-payment');
	const [showFinalizedModal, setShowFinalizedModal] = useState(false);
	const [commentValue, setCommentValue] = useState('');
	const [countRetry, setCountRetry] = useState<number>(0);

	const addCountRetry = () => {
		setCountRetry((prevCount) => prevCount + 1);
	};

	const resetCountRetry = () => {
		setCountRetry(0);
	};

	const paymentLinkMultiuseStates = ['actived', 'pending', 'expired', 'finalized'];
	const dropdownOptions = [
		{
			label: 'Editar',
			onClick: (rowData: PaymentMultiuse) => {
				setSelectedRowData(rowData);
				setShowEditModal(true);
				setViewType('edit-payment-link');
			},
		},
		{
			label: 'Finalizar',
			onClick: (rowData: PaymentMultiuse) => {
				setSelectedRowData(rowData);
				setShowFinalizedModal(true);
			},
			colorCustom: defaultTheme.colors.extra.sword,
		},
	];

	const columns: ColumnInterface<PaymentMultiuse>[] = [
		{
			sortField: true,
			title: 'Referencia',
			propertyName: 'reference',
			cellRender: ({ merchantorderid, status }) => (
				<>
					<Text disabled={status !== 'actived' && status !== 'pending'}>{merchantorderid}</Text>
				</>
			),
		},
		{
			sortField: true,
			title: 'Monto',
			align: 'right',
			propertyName: 'amount',
			cellRender: ({ amount, status }) => (
				<AmountContainer>
					<Text disabled={status !== 'actived' && status !== 'pending'}>{amount}</Text>
				</AmountContainer>
			),
		},
		{
			sortField: true,
			title: 'Cobros exitosos',
			align: 'center',
			propertyName: 'payments',
			cellRender: ({ numsuccesspayments, status }) => (
				<>
					<Text disabled={status !== 'actived' && status !== 'pending'}>
						{numsuccesspayments || 0}
					</Text>
				</>
			),
		},
		{
			title: 'Estatus',
			propertyName: 'status',
			align: 'center',
			cellRender: ({
				status,
				numpayments,
				endexpiredatdate,
				endexpiredattime,
				initexpiredatdate,
				initexpiredattime,
			}) => {
				return (
					<>
						<PaymentStatusComponent
							status={status}
							numpayments={numpayments}
							endexpiredatdate={endexpiredatdate}
							endexpiredattime={endexpiredattime}
							initexpiredatdate={initexpiredatdate}
							initexpiredattime={initexpiredattime}
						/>
					</>
				);
			},
		},
		{
			title: 'Tipo',
			propertyName: 'paymenttype',
			cellRender: ({ paymenttype, status }) => {
				return (
					<>
						{paymenttype.toLowerCase() === 'unique' ? (
							<UniquePayIcon disabled={status !== 'actived' && status !== 'pending'} />
						) : (
							<RecurrentPayIcon disabled={status !== 'actived' && status !== 'pending'} />
						)}
					</>
				);
			},
		},
		{
			sortField: true,
			title: 'Fecha de creación',
			propertyName: 'createdatDate',
			cellRender: ({ createdatdate, createdattime, status }) => (
				<Text disabled={status !== 'actived' && status !== 'pending'}>
					<DateAndTime date={createdatdate} time={createdattime} />
				</Text>
			),
		},
		{
			title: '',
			propertyName: '',
			cellRender: (rowData: PaymentMultiuse) => (
				<ContainerActionsComponent
					disabled={rowData.status === 'finalized' || rowData.status === 'expired'}
				>
					<ContainerActions>
						<SimpleButton
							onClick={() => {
								setSelectedRowData(rowData);
								setShowShareModal({ show: true, selected: 'send-link' });
								setViewType('share-payment-link');
							}}
						>
							<ShareIcon />
						</SimpleButton>
						<Dropdown disabled={true} options={dropdownOptions} rowData={rowData} />
					</ContainerActions>
				</ContainerActionsComponent>
			),
		},
	];

	const getStatusMessage = ({
		status,
		numpayments,
		endexpiredatdate,
		endexpiredattime,
		initexpiredatdate,
		initexpiredattime,
	}: GetStatusMessage) => {
		const statusMessages: Status[] = [
			{
				status: 'finalized',
				message: 'Este enlace está inactivo y no permite recibir más pagos',
			},
			{
				status: 'actived',
				message: `Este enlace podrá recibir hasta ${numpayments || 1} pagos ${
					endexpiredatdate ? `hasta el ${endexpiredatdate} ${endexpiredattime}` : ''
				}`,
			},
			{
				status: 'pending',
				message: `Este enlace podrá recibir pagos a partir del ${initexpiredatdate} ${initexpiredattime}`,
			},
			{
				status: 'expired',
				message: 'Este enlace ha finalizado sin recibir pagos exitosos',
			},
		];

		const selectedStatus = statusMessages.find((state) => state.status === status);
		return selectedStatus ? selectedStatus.message : '';
	};

	const PaymentStatusComponent = ({
		status,
		numpayments,
		endexpiredatdate,
		endexpiredattime,
		initexpiredatdate,
		initexpiredattime,
	}: {
		status: StatusType;
		numpayments: number;
		endexpiredatdate: string;
		endexpiredattime: string;
		initexpiredatdate: string;
		initexpiredattime: string;
	}) => {
		const message = getStatusMessage({
			status,
			numpayments,
			endexpiredatdate,
			endexpiredattime,
			initexpiredatdate,
			initexpiredattime,
		});

		return (
			<StatusContainer>
				<Tooltip text={message}>
					<Status status={status} borderFull={true} />
				</Tooltip>
			</StatusContainer>
		);
	};

	const columnsTableDetail: ColumnInterface<PaymentsMultiuse>[] = [
		{
			title: 'Identificador del pago',
			propertyName: 'status',
			cellRender: ({ paymentId }) => {
				return <TextPayment>{paymentId.slice(0, -54)}</TextPayment>;
			},
		},
		{
			sortField: true,
			title: 'RUT',
			align: 'center',
			propertyName: 'rut',
			cellRender: ({ rut }) => <>{rut}</>,
		},
		{
			sortField: true,
			title: 'Banco',
			propertyName: 'bankName',
			cellRender: ({ bankName }) => (
				<>{bankName === 'Desconocido' && merchantSelected?.country === 'MX' ? null : bankName}</>
			),
		},
		{
			title: 'Estado de la transición',
			align: 'center',
			propertyName: 'status',
			cellRender: ({ status }) => <Status status={status} size="sm" borderFull={true} />,
		},
		{
			sortField: true,
			title: 'Fecha de creación',
			propertyName: 'validateatDate',
			cellRender: ({ createdatDate, createdatTime }) => (
				<DateAndTime date={createdatDate} time={createdatTime} />
			),
		},
		{
			title: 'Comprobante',
			align: 'center',
			propertyName: 'urlCEP',
			cellRender: ({ urlCEP }) => (
				<VoucherContainer>
					{urlCEP && (
						<VoucherButton onClick={() => window.open(urlCEP)}>
							<EyeIcon />
						</VoucherButton>
					)}
				</VoucherContainer>
			),
		},
	];

	const tableWithDetails: RowInterface<PaymentMultiuse> = (data) => {
		return data.payments.length ? (
			<TableDetail columns={columnsTableDetail} data={data.payments} />
		) : undefined;
	};

	const handleExportAction = async () => {
		setModal({ ...modal, loader: true, show: true });

		try {
			const response = await PaymentLinksMultiuseServices.getExportData({
				merchantCode: merchantSelected?.code as string,
				dateRange: {
					from: filters?.dateRange?.from,
					to: filters?.dateRange?.to,
				},
				search: filters?.search,
				stateSelected: filters?.stateSelected,
			});

			if (response.url) {
				setModal({
					loader: false,
					show: true,
					error: false,
					errorMessage: '',
					url: response.url,
				});
				resetCountRetry();
			}
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: errorFormatter(e.code),
			});
			//Handle the add retry count
			addCountRetry();
			setCountRetry((prevCount) => {
				if (prevCount < 3) {
					setModal({
						...modal,
						loader: false,
						show: true,
						error: true,
						errorMessage:
							'Lo sentimos, ha ocurrido un error al intentar exportar los datos. Por favor, inténtalo nuevamente.',
						retryFunction: handleExportAction,
					});
				} else {
					setModal({
						...modal,
						loader: false,
						show: true,
						error: true,
						errorMessage: 'Lo sentimos, supero la cantidad de reintentos.',
						retryFunction: handleCloseModal,
					});
				}
				return prevCount;
			});
		}
	};

	const handleCloseModal = () => {
		setModal(defaultModal);
		resetCountRetry();
	};

	const handleDownloadData = () => {
		if (modal.url) window.location.assign(modal.url);
		setModal(defaultModal);
	};

	const onClose = () => {
		setShowEditModal(false);
		setShowShareModal({ show: false, selected: '' });
		setSelectedRowData(undefined);
	};

	const handleEndPaymentLink = async () => {
		setModal((modal) => ({ ...modal, loader: true, show: true }));
		try {
			await PaymentLinksMultiuseServices.endPaymentLinkMultiuse({
				merchantCode: merchantSelected?.code as string,
				presessionToken: selectedRowData?.token.split('/').slice(-1)[0] || '',
				comment: commentValue,
			});
			setShowFinalizedModal(false);
			resetCountRetry();
			refetch();
			handleShowAlert({
				message: 'Enlace de cobro finalizado',
				type: 'success',
			});
			setModal((modal) => ({ ...modal, loader: false, show: false, error: false }));
		} catch (e: any) {
			handleShowAlert({
				message: errorFormatter(e.code),
				type: 'error',
			});
			setShowFinalizedModal(false);
			addCountRetry();
			setCountRetry((prevCount) => {
				if (prevCount < 3) {
					setModal({
						...modal,
						loader: false,
						show: true,
						error: true,
						errorMessage:
							'Lo sentimos, ha ocurrido un error al intentar finalizar el link de cobro. Por favor, inténtalo nuevamente.',
						retryFunction: handleEndPaymentLink,
					});
				} else {
					setModal({
						...modal,
						loader: false,
						show: true,
						error: true,
						errorMessage: 'Lo sentimos, supero la cantidad de reintentos.',
						retryFunction: handleCloseModal,
					});
				}
				return prevCount;
			});
		}
	};

	const renderSelectByType = () => (
		<SelectContainer>
			<Select
				options={[
					{ label: 'Todos', value: '', customLabel: 'Todos', show: true },
					{
						label: (
							<ProductFilterLabel>
								<UniquePayIcon /> Únicos
							</ProductFilterLabel>
						),
						value: 'UNIQUE',
						customLabel: 'Únicos',
						show: true,
					},
					{
						label: (
							<ProductFilterLabel>
								<RecurrentPayIcon /> Recurrentes
							</ProductFilterLabel>
						),
						value: 'RECURRENT',
						customLabel: 'Recurrentes',
						show: true,
					},
				]}
				prefix="Tipo:"
				onChange={({ value }) => {
					if (onFilterChange) {
						onFilterChange({ ...filters, typeSelected: value as string });
					}
				}}
			/>
		</SelectContainer>
	);

	const renderContentModal = () => {
		if (modal.loader) {
			return <Spinner />;
		} else if (modal.error) {
			return (
				<ExportModalContent>
					<ExportModalTitle>Ha ocurrido un error</ExportModalTitle>
					<ExportModalDescription>
						{modal.errorMessage || 'Lo sentimos, ha ocurrido un error inesperado'}
					</ExportModalDescription>
					<ETPayButton
						title={countRetry < 3 ? 'Reintentar' : 'Cerrar'}
						color="secondary"
						onClick={modal.retryFunction}
					/>
				</ExportModalContent>
			);
		} else {
			return (
				<ExportModalContent>
					<ExportModalTitle>Tu reporte está listo</ExportModalTitle>
					<ExportModalDescription>
						Hemos terminado de recopilar los datos de tu reporte. Haz clic en el botón para
						descargarlo.
					</ExportModalDescription>
					<ETPayButton title="Descargar reporte" variant="effect" onClick={handleDownloadData} />
				</ExportModalContent>
			);
		}
	};

	const renderModal = () => {
		return (
			<Modal
				title={modal.error ? 'Error' : 'Exportar datos'}
				show={modal.show}
				onClose={handleCloseModal}
				allowCloseModal={!modal.loader}
			>
				{renderContentModal()}
			</Modal>
		);
	};

	return (
		<>
			{modal.show &&
				hasPermission('PL_EXPORT', merchantSelected?.role_permissions) &&
				hasPermission('PL_MULTIUSE_EXPORT', merchantSelected?.role_permissions) &&
				renderModal()}
			{(showShareModal.show || showEditModal) && (
				<NewSinglePayment
					show={showEditModal || showShareModal.show}
					onClose={onClose}
					selectedToEdit={selectedRowData}
					viewType={viewType}
					refetch={refetch}
				/>
			)}
			{showFinalizedModal && (
				<ModalMultiuse
					show={showFinalizedModal}
					setShowFinalizedModal={() => setShowFinalizedModal(false)}
					handleEndPaymentLink={() => handleEndPaymentLink()}
					setCommentValue={setCommentValue}
					commentValue={commentValue}
					modal={modal}
				/>
			)}

			<PLTable<PaymentMultiuse>
				title="Todos los enlaces de cobro"
				data={data}
				isLoading={!!isLoading}
				columns={columns}
				row={tableWithDetails}
				customFilter={renderSelectByType()}
				hasFilters
				hasDateFilter
				allowExport={
					hasPermission('PL_EXPORT', merchantSelected?.role_permissions) &&
					hasPermission('PL_MULTIUSE_EXPORT', merchantSelected?.role_permissions)
				}
				stateList={fillStatesWithZeros({ states: paymentLinkMultiuseStates, stateList })}
				filters={filters}
				onFilterChange={onFilterChange}
				hasSearch
				exportAction={handleExportAction}
				hasPagination
				allowCustomFilter
				totalResults={totalResults}
				pagination={pagination}
				setPagination={setPagination}
				statusContent={[
					['actived', 'Activos'],
					['pending', 'Pendiente'],
					['finalized', 'Finalizado'],
					['expired', 'Expirados'],
				]}
			/>
		</>
	);
};

export { PaymentLinkTable };
